import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AppShell, useAskOne } from '../components/AppShell'
import { HelpPage } from '../components/HelpPage'
import { supabase } from '../lib/supabase'
import { can, canGallery, type Me } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { initialsOf } from '../lib/initials'
import { myLanes } from '../lib/support'
import { parseHelpSeed } from '../lib/helpLink'

/**
 * #/help — ONE PAGE FOR REACHING A PERSON (337, HLP-43; One and Help v2 S1).
 *
 * one-and-help-v2.html in the docs repo is the design. Ask One answers; Help is
 * where you reach a person or tell us something. The New menu lists Ask One
 * FIRST, so One stays the first door even here (the 16 Sep argument, met
 * rather than ignored). A ticket goes to the team the PERSON picks, Platform
 * support pre-selected (D2); feedback goes to the platform desk (D3).
 *
 * The ticket form renders the pieces that used to be the drawer's routing step
 * (IntentChoices, RouteQuestionFields, the pictures, the HR guidance) and
 * sends through the one `useRouting` (components/SupportSend.tsx). S2 (31 Oct)
 * took them out of the drawer: Ask One links HERE, prefilled, and the URL's
 * request is read once by `parseHelpSeed` - a request, never a permission.
 */
export function Help({ me }: { me: Me }) {
  return (
    <AppShell
      current="Help"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {/* A CHILD of the shell, never the route body: useAskOne() above the
          provider is the no-op that broke #/wiki for five days (AppShell). */}
      <HelpBody me={me} />
    </AppShell>
  )
}

function HelpBody({ me }: { me: Me }) {
  const openAsk = useAskOne()
  const [params, setParams] = useSearchParams()
  // Read once: the page keeps the seed in its own state, and the URL's copy is
  // dropped (replace, not push) when the seeded form closes.
  const [seed] = useState(() => parseHelpSeed(params))
  return (
    <HelpPage
      me={me}
      onAskOne={() => openAsk()}
      seed={seed}
      onSeedUsed={() => setParams({}, { replace: true })}
    />
  )
}

